import { Router } from 'vue-router'
import { h, Ref } from 'vue'
import { orderBy, cloneDeep, sortedIndexBy } from 'lodash'

import { EntityState } from '@/entities/utils/enums'
import { Entity } from '@/entities/utils/common'

import { UNKNOWN_VALUE } from '@/const/common'

import { useNotifications } from '@/plugins/notification'
import { generateId } from '@/entities/utils/helpers'

const findItemIndex = <T extends Record<string, any>>(
  storeList: T[],
  value: string | undefined,
  keyField = 'id' as keyof T,
) => storeList.findIndex(item => item[keyField] === value)

export const createBunchElement = <T extends Entity<any>>(
  EntryClass: new (data: Partial<T>, created: boolean, strictValidation?: boolean) => T,
  data: Record<string, any>,
  strictValidation?: boolean,
) => {
  const id = generateId()
  return new EntryClass({ id, ...data } as Partial<T>, true, strictValidation)
}

export const insertBunchElementAt = <T extends Entity<any>>(
  element: T,
  list: Ref<Map<string, any>>,
  index = 0,
) => {
  const entries = Array.from(list.value.entries())
  const normalizedIndex = Math.max(0, Math.min(index, entries.length))
  entries.splice(normalizedIndex, 0, [element.id, element])
  list.value = new Map(entries)
  return element
}

export const unshiftBunchElement = <T extends Entity<any>>(
  element: T,
  list: Ref<Map<string, any>>,
) => insertBunchElementAt(element, list)

export const duplicateBunchElement = <T extends Entity<any>>(
  id: string,
  list: Ref<Map<string, any>>,
  EntryClass: new (data: Partial<T>, created: boolean, strictValidation?: boolean) => T,
  strictValidation?: boolean,
) => {
  const entries = Array.from(list.value.entries())
  const index = entries.findIndex(item => item[0] === id)
  if (index > -1) {
    const id = generateId()
    const entry = entries[index][1]
    const data = entry.get()
    const element = new EntryClass({
      ...data,
      id,
      ...(data.name ? { name: `${data.name} (copy)` } : {}),
      ...(data.ticker ? { ticker: `${data.ticker} (copy)` } : {}),
      created_at: undefined,
      created_by: undefined,
      updated_at: undefined,
      updated_by: undefined
    }, true, strictValidation)
    const prefixBunch = entries.slice(0, index + 1)
    const postfixBunch = entries.slice(index + 1)
    list.value = new Map([...prefixBunch, [id, element], ...postfixBunch])
  }
}

export const addStoreListItem = <T extends Record<string, any>>(
  storeList: T[],
  processingItem: T,
  sortedField?: keyof T,
  formatter?: (value: any) => any,
) => {
  if (sortedField) {
    const insertItemToIndex = sortedIndexBy(
      storeList,
      { [sortedField]: processingItem[sortedField] } as any,
      item => (formatter ? formatter(item[sortedField]) : item[sortedField]),
    )
    storeList.splice(insertItemToIndex, 0, processingItem)
  } else {
    storeList.push(processingItem)
  }
}

export const updateStoreListItem = <T extends Record<string, any>>(
  storeList: T[],
  processingItem: Partial<T>,
  keyField = 'id' as keyof T,
) => {
  const currentItemIndex = findItemIndex<T>(
    storeList,
    processingItem[keyField],
    keyField,
  )
  if (currentItemIndex === -1) return
  const keys = Object.keys(processingItem).filter(key => key !== keyField)
  keys.forEach(key => {
    // @ts-expect-error - storeList is an array of objects
    storeList[currentItemIndex][key] = processingItem[key]
  })
}

export const removeStoreListItem = <T extends Record<string, any>>(
  storeList: T[],
  value: string,
  keyField = 'id' as keyof T,
) => {
  const currentItemIndex = findItemIndex<T>(storeList, value, keyField)
  storeList.splice(currentItemIndex, 1)
}

export const getBunchInsertIndex = <T extends { id: string }>(
  list: Map<string, T>,
  visibleItems: T[],
  index: number,
) => {
  const allItems = Array.from(list.values())

  const nextItem = visibleItems[index]
  if (nextItem) {
    const nextIndex = allItems.findIndex(item => item.id === nextItem.id)
    if (nextIndex > -1) return nextIndex
  }

  const previousItem = visibleItems[index - 1]
  if (previousItem) {
    const previousIndex = allItems.findIndex(item => item.id === previousItem.id)
    if (previousIndex > -1) return previousIndex + 1
  }

  return Math.max(0, Math.min(index, allItems.length))
}

export const prepareResponseError = (e: any) =>
  e?.message ? e.message : String(e)

export const runMigrations = async (settings: Record<string, any>, migrations: Record<string, any>) => {
  const dateRegex = new RegExp(/\/(\d{4}\d{2}\d{2})/)
  const version = settings.version || '19700101'
  const orderedList = orderBy(Object.keys(migrations))
  const lastMigration = orderedList.at(-1)
  const lastMigrationDate = lastMigration && dateRegex.exec(lastMigration)?.[1]
  if (!lastMigrationDate) return null
  const newSettings = cloneDeep(settings)
  if (+version < +lastMigrationDate) {
    let findIndex = version ? orderedList.findIndex(migration => {
      const migrationDate = dateRegex.exec(migration)
      return migrationDate?.[1] === version
    }) : -1
    const migrationsToRun = orderedList.slice(findIndex + 1).map(migration => migrations[migration])
    if (!migrationsToRun.length) return null
    for (const migration of migrationsToRun) {
      await migration.default(newSettings)
    }
  }
  if (!lastMigration || lastMigrationDate === version) return null
  newSettings.version = lastMigrationDate
  return Object.entries(newSettings)
}

const isDirtyState = (data: EntityState) => {
  return [
    EntityState.CHANGED,
    EntityState.NEW,
    EntityState.INVALID,
  ].includes(data)
}

export const isDirtyList = <T extends { state: EntityState; isChanged: boolean }>(
  data: Map<string, T>,
) => {
  for (const value of data.values()) {
    if (
      isDirtyState(value.state) &&
      // if new and invalid, don't mark as dirty
      !(!value.isChanged && value.state === EntityState.INVALID)
    ) return true
  }
  return false
}

export const createLink = (
  id: string,
  name: string,
  router: Router,
  routeName: string,
) => {
  return h(
    'span',
    {
      class: 'text-indigo-500 hover:text-indigo-600 cursor-pointer',
      onClick: () => {
        router.push({
          name: routeName,
          params: { id },
        })
      },
    },
    name || UNKNOWN_VALUE,
  )
}

export const unsavedGridBlocker =
  (gridName: string, stayCallback: () => void, cancelCallback?: () => void) => () =>
    new Promise<boolean>((resolve, reject) => {
      const { warn } = useNotifications()
      warn({
        message: 'Warning',
        description: `You have unsaved changes in the ${gridName} grid, do you want to continue editing?`,
        actionRequired: true,
        actions: [
          {
            label: 'Continue editing',
            buttonVariant: 'primary',
            onClick: remove => {
              remove && remove()
              stayCallback()
              reject()
            },
          },
          {
            label: 'Discard changes',
            buttonVariant: 'secondary',
            onClick: remove => {
              remove && remove()
              cancelCallback?.()
              resolve(true)
            },
          },
        ],
      })
    })

export const trimObjectValues = <T extends object>(data: T, fields: string[]) => {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [key, fields.includes(key) ? value.trim() : value]),
  ) as T
}
